import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import { type PrimitiveAtom, useAtomValue } from "jotai";
import { type MouseEvent, useMemo } from "react";
import { useTranslation } from "src/i18n";
import { CellElement } from "src/elements/SubgridTable/components/SubgridCell";
import styled from "styled-components";
import { styledSafe } from "src/utils/styledComponent";
import { LandscapeA4WidthInPx } from "./constant";
import { FirstPageSection } from "./FirstPageSection";
import { buildItemSummaryPages } from "./ItemSummaryPage";
import { ItemSummaryPagesList } from "./ItemSummaryPagesList";
import type { StoreProductsMap, StoreDetail } from "./PageMerchflows_ExportPdf_StorePogId";
import { SingleBayPagesList } from "./SingleBayPagesList";
import { buildA4PagesForSingleBay } from "./singleBayUtils";
import { DEFAULT_NOTCH_RULER_WIDTH } from "src/components/Planogram/components/Ruler/PdfNotchRulers";
import type { BayWithA4Pages } from "./types";

const View = styled.div`
  --shelf-color: gray;
  width: ${LandscapeA4WidthInPx};
  height: auto;
  padding-inline: 12px;
  box-sizing: border-box;

  ${CellElement} {
    padding: 2px;
  }
`;

export const PlanogramContainer = styledSafe("div")<{
  isRenderingAsImage?: boolean;
}>`
  width: 100%;
  height: 100%;
  flex-grow: 1;
  user-select: none;
  visibility: ${({ isRenderingAsImage }) => isRenderingAsImage && "hidden"};
`;

export const PlanogramElement = styled.div<{
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
}>`
  width: ${({ width, scaleX }) => `calc(${width}px * ${scaleX})`};
  height: ${({ height, scaleY }) => `calc(${height}px * ${scaleY})`};
  background-color: #fcf7f0;
  position: relative;
`;

interface Props {
  currentStoreCode?: string;
  pogAtom: PrimitiveAtom<Pog | null>;
  storeDetail: StoreDetail | null;
  storeProducts?: StoreProductsMap;
  hasNoDeliveryHistory?: boolean;
  hasComparisonError?: boolean;
}

interface PlanogramInnerProps extends Props {
  pog: Pog;
}

export const PdfPlanogramPage = (props: Props) => {
  const pog = useAtomValue(props.pogAtom);

  return pog ? (
    <PlanogramInner
      currentStoreCode={props.currentStoreCode}
      pogAtom={props.pogAtom}
      storeDetail={props.storeDetail}
      storeProducts={props.storeProducts}
      hasNoDeliveryHistory={props.hasNoDeliveryHistory}
      hasComparisonError={props.hasComparisonError}
      pog={pog}
    />
  ) : null;
};

const PlanogramInner = ({
  currentStoreCode,
  pogAtom,
  storeDetail,
  storeProducts,
  hasNoDeliveryHistory,
  hasComparisonError,
  pog,
}: PlanogramInnerProps) => {
  const { t } = useTranslation("common");
  const { scaleX, scaleY } = useMemo(() => {
    const scale = 492 / pog.planogram.getMaxHeight();
    return { scaleX: scale, scaleY: scale };
  }, [pog]);

  const disableContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const processedBay: BayWithA4Pages[] = useMemo(() => {
    return pog.planogram.bays.map((currentBay) => ({
      bay: currentBay,
      pages: buildA4PagesForSingleBay({
        pog,
        bayId: currentBay.uniqueId,
        section: currentBay.getNo(),
        t,
      }),
    }));
  }, [pog, t]);

  const firstPageWidth =
    pog.planogram.getWidth() + pog.planogram.bays.length * 2 * DEFAULT_NOTCH_RULER_WIDTH;
  const firstPageHeight = pog.planogram.getMaxHeight();
  const firstPageScale =
    firstPageWidth > 0 && firstPageHeight > 0
      ? Math.min(1076 / firstPageWidth, 492 / firstPageHeight)
      : 1;

  const singleBayPagesFlat = useMemo(() => {
    return processedBay.flatMap((bayWithPages) =>
      bayWithPages.pages.map((page, index) => ({
        bay: bayWithPages.bay,
        page,
        index,
      })),
    );
  }, [processedBay]);

  const itemSummaryPages = useMemo(() => {
    return buildItemSummaryPages({
      pog,
      storeDetail,
      currentStoreCode,
      storeProducts,
      hasNoDeliveryHistory,
      hasComparisonError,
    });
  }, [pog, storeDetail, currentStoreCode, storeProducts, hasNoDeliveryHistory, hasComparisonError]);

  const itemSummaryStartPageNumber = singleBayPagesFlat.length + 2;
  const totalPages = singleBayPagesFlat.length + 1 + itemSummaryPages.length;

  return (
    <View onContextMenuCapture={disableContextMenu}>
      <FirstPageSection
        storeDetail={storeDetail}
        pogAtom={pogAtom}
        firstPageScale={firstPageScale}
        totalPages={totalPages}
        scaleX={scaleX}
        scaleY={scaleY}
      />
      <SingleBayPagesList
        singleBayPagesFlat={singleBayPagesFlat}
        storeDetail={storeDetail}
        scaleX={scaleX}
        scaleY={scaleY}
        pog={pog}
        totalPages={totalPages}
      />
      <ItemSummaryPagesList
        itemSummaryPages={itemSummaryPages}
        storeDetail={storeDetail}
        itemSummaryStartPageNumber={itemSummaryStartPageNumber}
        totalPages={totalPages}
      />
    </View>
  );
};
